import { useNotification } from "@context/NotificationContext";
import {
  drybulkCargoOperationsService,
  type StagedAttachment,
} from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
} from "../constants";
import { problemDetail } from "../utils/problemDetail";

/**
 * DBCO-0030 (Q1) - attaches the plans staged on the Add page, one `POST .../attachments` each, in the order given
 * and one business transaction per plan. `onAttached` lets the page clear a slot as soon as its plan is attached,
 * so a retry after a failure sends only what is left. The first refusal shows the server's detail and stops
 * (resolving false, which stops the save or the submit).
 */
export const useAttachStagedPlans = ({
  onAttached,
}: { onAttached?: (attachmentType: string) => void } = {}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const attach = useMutation({
    mutationFn: ({ operationId, staged }: { operationId: string; staged: StagedAttachment }) =>
      runTransaction(
        {
          module: DRYBULK_CARGO_OPS_TELEMETRY.module,
          screen: DRYBULK_CARGO_OPS_TELEMETRY.addScreen,
          action: "attach_cargo_operation_plan",
          entityType: "cargo_operation",
          entityId: operationId,
        },
        (transactionId) =>
          drybulkCargoOperationsService.uploadAttachment(operationId, staged, { transactionId }),
      ),
  });

  const attachStaged = async (
    operationId: string,
    staged: StagedAttachment[],
  ): Promise<boolean> => {
    if (staged.length === 0) return true;
    try {
      for (const plan of staged) {
        await attach.mutateAsync({ operationId, staged: plan });
        onAttached?.(plan.attachmentType);
      }
      return true;
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description:
          problemDetail(error) ?? t(`${DRYBULK_CARGO_OPS_LANG_BASE}.documents.attach-failed`),
      });
      return false;
    } finally {
      void queryClient.invalidateQueries({
        queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId),
      });
    }
  };

  return { attachStaged };
};
